/**
 * Shared text-level fixes applied across every surface that renders
 * AI-drafted clinical prose — the markdown→HTML converter, the editor's
 * legacy-HTML post-processor, and the read-only NdjsonBlockRenderer.
 * One helper, three consumers — same regex, same M:SS math, same
 * quote-emphasis rule everywhere a clinician might read the text.
 */

/**
 * Render a session-relative offset as a human-readable mark.
 *
 * AI drafts emit `@ 32140.0s` — the `s` suffix is misleading; the
 * underlying unit is milliseconds (Sarvam reports `startMs`). We treat
 * any value above two hours of seconds (>7200) as milliseconds; smaller
 * values stay seconds. Under a minute → `Ns`; otherwise → `M:SS`.
 */
export function formatSessionOffset(raw: number): string {
  const seconds = raw > 7200 ? raw / 1000 : raw;
  if (seconds < 60) return `${String(Math.round(seconds))}s`;
  const m = Math.floor(seconds / 60);
  const s = Math.round(seconds % 60);
  return `${String(m)}:${String(s).padStart(2, '0')}`;
}

/**
 * The `@ <ms>s` pattern AI drafts emit for evidence timestamps, plus the
 * replacement that rewrites them to readable `· at M:SS` marks. Shared
 * between the markdown preprocessor and the HTML postprocessor so the
 * two surfaces can't drift apart — change the format here, both paths
 * update.
 */
const SESSION_OFFSET_PATTERN = /@\s*(\d+(?:\.\d+)?)s\b/g;
function replaceSessionOffset(_match: string, ms: string): string {
  return `· at ${formatSessionOffset(parseFloat(ms))}`;
}

/**
 * Pre-process raw AI markdown text before it's rendered by `marked` or
 * `react-markdown`. Two clinical-prose transformations:
 *
 * 1. `@ 32140.0s` → `· at 0:32` — readable session offset.
 * 2. Quoted speech (both straight `"..."` and smart `“...”`) gets
 *    wrapped in markdown emphasis so the rendered HTML carries
 *    `<em>` around the quote, giving CSS a hook to style it as a
 *    printed-page excerpt.
 */
export function preprocessClinicalMarkdown(text: string): string {
  return text
    .replace(SESSION_OFFSET_PATTERN, replaceSessionOffset)
    .replace(/"([^"\n]+)"/g, '*"$1"*')
    .replace(/“([^”\n]+)”/g, '*“$1”*');
}

/**
 * Apply readability fixes to already-rendered HTML (drafts that were
 * opened in the editor and autosaved as HTML once — the original
 * NDJSON has been replaced by Tiptap output). Only the timestamp
 * regex runs here; rewriting quotes mid-document risks corrupting
 * adjacent markup, so quote emphasis applies to fresh markdown only.
 */
export function postprocessClinicalHtml(html: string): string {
  return html.replace(SESSION_OFFSET_PATTERN, replaceSessionOffset);
}
